import React, { isValidElement, useCallback, memo } from 'react';

import clsx from 'clsx';
import { useResourceContext, useRecordContext, RowClickFunction } from 'react-admin';
import { DatagridCell, DatagridClasses, DatagridField } from 'react-admin';
import { TableRow } from 'shared/mui/Table';
import { DatagridRowProps } from 'shared/react-admin/DatagridRows/DatagridRow/DatagridRow';

import { BalanceRow } from './BalanceReportDatagrid';

type Props = Omit<DatagridRowProps, 'component' | 'sx'> & {
  rowClick?: RowClickFunction | undefined;
};

const BalanceReportPureRow: React.ForwardRefExoticComponent<
  Omit<Props, 'ref'> & React.RefAttributes<HTMLTableRowElement>
> = React.forwardRef<HTMLTableRowElement, Props>((props, ref) => {
  const {
    children,
    className,
    hover = true,
    id,
    rowClick,
    style,
    selectable = true,
    ...rest
  } = props;

  const record = useRecordContext<BalanceRow>();
  const resource = useResourceContext();

  const handleClick = useCallback(() => {
    if (record?.id && rowClick) {
      rowClick(record?.id, String(resource), record);
    }
  }, [rowClick, resource, record]);

  const getRowClassName = () => {
    switch (record?.type) {
      case 'company':
        return `row-company-${record?.index % 2}`;
      case 'provider':
        return `row-provider-${record?.index % 6}`;
      default:
        return `row-${record?.type}`;
    }
  };

  return (
    <TableRow
      className={clsx(className, getRowClassName(), {
        [DatagridClasses.selectable]: selectable,
        [DatagridClasses.clickableRow]: record?.type !== 'account',
      })}
      hover={hover}
      key={id}
      onClick={handleClick}
      ref={ref}
      style={style}
      {...rest}
    >
      {React.Children.map(children, (field, index) =>
        isValidElement(field) ? (
          <DatagridCell
            className={clsx(
              `column-${(field as DatagridField).props.source}`,
              DatagridClasses.rowCell,
            )}
            key={`${id}-${(field as DatagridField).props.source || index}`}
            record={record}
            {...{
              field: field as DatagridField,
              resource,
            }}
          />
        ) : null,
      )}
    </TableRow>
  );
});

export const BalanceReportDatagridRow = memo(BalanceReportPureRow);

BalanceReportPureRow.displayName = 'BalanceReportDatagridRow';
